import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { Modal, Box, ActionIcon, Select } from '@mantine/core'
import { IconX, IconChevronDown, IconSearch, IconUserPlus, IconCalendar } from '@tabler/icons-react'
import { buildTimeOptions, getNextTimeSlotEnding, updateDateTime24 } from '../../utils'
import { Patient } from '@medplum/fhirtypes'
import { formatPatientName } from '@/utils.ts'
import { useDebounce } from '@/hooks'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import AddNewPatientModal from '@/components/ui/CreateUpdatePatientModal/CreateUpdatePatientModal'
import { buildPatientLookupSearchQuery } from '@/utils/patientSearch'
import { VISIT_REASONS } from '@/model/middleware-types'

export type AppointmentModalPayload = {
  status: string
  patientId?: string | null
  start: string
  end: string
  /** Reason-for-visit catalog id; optional — staff often book before knowing it. */
  reasonId?: string | null
}

const timeOptions = buildTimeOptions(8, 20, 30)

// Grouped for the Select's optgroups, in the catalog's own order.
const reasonOptions = [...new Set(VISIT_REASONS.map((r) => r.group))].map((group) => ({
  group,
  items: VISIT_REASONS.filter((r) => r.group === group).map((r) => ({
    value: r.id,
    label: r.label,
  })),
}))

// Segmented tab pill — mirrors the Schedule page Day/Week/Month view selector
// (CalendarViewSwitcher): coral-filled active tab, full-width segments.
const tabBase =
  'flex-1 cursor-pointer rounded-md border-none px-4 py-[1.5px] !text-[16px] leading-none !font-semibold transition-all ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
const inactive = 'bg-transparent font-semibold text-[#6B7280]'
const active = 'bg-[#E5533D] font-bold text-white shadow-[0_1px_2px_rgba(203,71,31,.3)]'

export function AppointmentModal({
  opened,
  onClose,
  selectedSlotTime,
  processTimeAllocation,
}: {
  opened: boolean
  onClose: () => void
  selectedSlotTime: Date | string
  processTimeAllocation: (payload: AppointmentModalPayload) => void
}) {
  const medplum = useMedplum()
  const [activeTab, setActiveTab] = useState('appointment')
  const [slotStart, setSlotStart] = useState<string | null>(
    new Date(selectedSlotTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  )
  const [slotEnd, setSlotEnd] = useState(slotStart)
  const [selectedPatient, setSelectedPatient] = useState<string | null>(null)
  const [reasonId, setReasonId] = useState<string | null>(null)

  const [patients, setPatients] = useState<Patient[]>([])
  const [searchQueryText, setSearchQueryText] = useState('')
  const debouncedSearchQuery = useDebounce(searchQueryText, 500)
  const openANPModal = () => setIsAddPatientModalOpen(true)
  const closeANPModal = () => setIsAddPatientModalOpen(false)
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false)
  const [searchIsLoading, setSearchIsLoading] = useState(false)
  const [selectPatientOptions, setSelectPatientOptions] = useState<
    { value: string; label: string }[]
  >([])

  const formattedSlotDateStr = new Intl.DateTimeFormat('en-US', {
    weekday: 'short', // "Mon", "Tue", ...
    month: 'short', // "Jan", "Feb", ...
    day: 'numeric', // 1, 2, ...
  }).format(typeof selectedSlotTime === 'string' ? new Date(selectedSlotTime) : selectedSlotTime)

  const createNewPatientCallBack = (patient: Patient) => {
    setPatients([patient])
    handlePatientChange(patient?.id || null)
  }
  const handleSlotStartTimeUpdate = (timeOption: string | null) => {
    setSlotStart(timeOption)
    if (
      timeOption &&
      updateDateTime24(selectedSlotTime, timeOption) >=
        updateDateTime24(selectedSlotTime, slotEnd || '')
    ) {
      const nextEndTime = timeOption ? getNextTimeSlotEnding(timeOption) : timeOption
      setSlotEnd(nextEndTime)
    }
    if (activeTab === 'appointment' && timeOption) {
      setSlotEnd(getNextTimeSlotEnding(timeOption))
    }
  }

  const handleSlotEndTimeUpdate = (timeOption: string | null) => {
    setSlotEnd(timeOption)
    if (activeTab === 'appointment') {
      const indexOfEndOption = timeOptions.findIndex((opt) => opt.value === timeOption)
      setSlotStart(timeOptions[indexOfEndOption - 1].value)
    }
  }
  const handlePatientChange = (patientOption: string | null) => {
    setSelectedPatient(patientOption)
  }

  function handleCloseModal(): void {
    setActiveTab('appointment')
    setSelectedPatient(null)
    setReasonId(null)
    const nextEndTime = getNextTimeSlotEnding(slotStart || '')
    setSlotEnd(nextEndTime)
    onClose()
  }

  function handleTimeSlotAllocation(): void {
    if (!slotStart || !slotEnd) return

    let payload
    if (activeTab === 'appointment') {
      payload = {
        status: 'appointment',
        patientId: selectedPatient,
        start: updateDateTime24(selectedSlotTime, slotStart),
        end: updateDateTime24(selectedSlotTime, slotEnd),
        reasonId,
      }
    } else if (activeTab === 'block') {
      payload = {
        start: updateDateTime24(selectedSlotTime, slotStart),
        end: updateDateTime24(selectedSlotTime, slotEnd),
        status: 'busy-unavailable',
      }
    } else {
      payload = {
        start: updateDateTime24(selectedSlotTime, slotStart),
        end: updateDateTime24(selectedSlotTime, slotEnd),
        status: 'free',
      }
    }
    processTimeAllocation(payload)
    handleCloseModal()
  }

  useEffect(() => {
    let cancelled = false

    if (debouncedSearchQuery.length > 2) {
      setSearchIsLoading(true)
      medplum
        .search('Patient', buildPatientLookupSearchQuery(debouncedSearchQuery))
        .then((result) => {
          if (cancelled) return
          const patientList = result.entry?.map((entry) => entry.resource as Patient) || []
          setPatients(patientList)
        })
        .catch((e) => {
          if (cancelled) return
          logError('schedule', 'Failed to fetch patients', e)
          showNotification({
            title: 'Error',
            message: 'Failed to fetch patients',
            color: 'red',
          })
        })
        .finally(() => {
          if (!cancelled) {
            setSearchIsLoading(false)
          }
        })
    } else {
      setPatients([])
      setSelectPatientOptions([])
      setSearchIsLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [medplum, debouncedSearchQuery])
  useEffect(() => {
    if (!selectedSlotTime) return

    const neededDate = new Date(selectedSlotTime)
    const hours = neededDate.getHours()
    const minutes = neededDate.getMinutes()
    const hoursString = hours < 10 ? '0' + hours : hours
    const minutesStr = minutes < 10 ? '0' + minutes : minutes

    setSlotStart(`${hoursString}:${minutesStr}`)
    setSlotEnd(getNextTimeSlotEnding(`${hoursString}:${minutesStr}`))
  }, [selectedSlotTime])

  useEffect(() => {
    if (patients) {
      const filteredOptions = patients.map((patient) => {
        return {
          value: patient.id!,
          label: patient.name?.[0] ? formatPatientName(patient.name[0]) : '',
        }
      })
      setSelectPatientOptions(filteredOptions)
    } else {
      setSelectPatientOptions([])
    }
  }, [patients])

  const timeInputClass = 'h-[42px] rounded-lg border border-[#D8DBE0] text-sm font-semibold'
  const saveDisabled = !slotStart || !slotEnd || (activeTab === 'appointment' && !selectedPatient)

  return (
    <Modal
      className="appointment-modal"
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      classNames={{
        content: 'schedule-v2-scope rounded-[18px] shadow-[0_24px_60px_rgba(16,24,40,0.28)]',
        body: 'p-[22px]',
      }}
      radius={18}
      size="520px"
    >
      {/* Segmented tabs (almost full-width) share one row with the close button */}
      <Box className="mb-5 flex items-center gap-2.5">
        <Box
          role="radiogroup"
          aria-label="Slot type"
          className="schedule-v2-mulish flex flex-1 items-center gap-0.5 rounded-lg bg-[#F1F2F4] p-1"
        >
          <button
            type="button"
            role="radio"
            aria-checked={activeTab === 'appointment'}
            onClick={() => {
              setActiveTab('appointment')
              if (slotStart) {
                setSlotEnd(getNextTimeSlotEnding(slotStart))
              }
            }}
            className={`${tabBase} ${activeTab === 'appointment' ? active : inactive}`}
          >
            Appointment
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={activeTab === 'availability'}
            onClick={() => setActiveTab('availability')}
            className={`${tabBase} ${activeTab === 'availability' ? active : inactive}`}
          >
            Availability
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={activeTab === 'block'}
            onClick={() => setActiveTab('block')}
            className={`${tabBase} ${activeTab === 'block' ? active : inactive}`}
          >
            Block
          </button>
        </Box>
        <button
          type="button"
          title="Close"
          onClick={handleCloseModal}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
        >
          <IconX size={17} stroke={2.1} />
        </button>
      </Box>

      {activeTab === 'appointment' && (
        <Box className="mb-5 flex flex-col">
          <label className="mb-2 text-[13px] font-bold text-[#4A5160]">Select patient</label>
          <Select
            searchValue={searchQueryText}
            onSearchChange={setSearchQueryText}
            onFocus={() => setSelectPatientOptions([])}
            searchable
            data={selectPatientOptions}
            value={selectedPatient}
            name={`patient-name`}
            onChange={handlePatientChange}
            classNames={{
              input: 'h-[42px] rounded-lg border border-[#D8DBE0] text-sm',
            }}
            leftSection={<IconSearch size={16} className="opacity-60" />}
            rightSection={
              <div className="flex items-center gap-1">
                {(selectedPatient || searchQueryText) && (
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => {
                      setSelectedPatient(null)
                      setSearchQueryText('')
                      setSelectPatientOptions([])
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )}
                <ActionIcon
                  variant="transparent"
                  onClick={openANPModal}
                  title="Add new patient"
                  className="!text-[#E5533D] hover:!bg-[#FDECE8]"
                >
                  <IconUserPlus size={16} />
                </ActionIcon>
              </div>
            }
            placeholder="Patient name or phone…"
            comboboxProps={{ withinPortal: true }}
            allowDeselect={false}
            rightSectionPointerEvents="all"
            rightSectionWidth={selectedPatient || searchQueryText ? 56 : 32}
            nothingFoundMessage={
              searchQueryText.length > 1 &&
              patients.length === 0 &&
              !searchIsLoading && (
                <div className="space-between justify-space-between flex w-full items-center gap-2 p-0">
                  <p>No patients found</p>
                </div>
              )
            }
          />

          {/* Reason for visit — mirrors the patient portal's picker (it writes
              Encounter.reasonCode[0]), but optional here: staff book by phone and
              often do not have the reason yet. */}
          <label className="mt-4 mb-2 text-[13px] font-bold text-[#4A5160]">
            Reason for visit <span className="font-medium text-[#8A929C]">(optional)</span>
          </label>
          <Select
            name="appointment-reason"
            data={reasonOptions}
            value={reasonId}
            onChange={setReasonId}
            searchable
            clearable
            // Mantine's ScrollArea wraps the options in a `display: table` element,
            // which stops `position: sticky` from working. Turning it off makes the
            // options list itself the scroll container (native scrollbar), so each
            // group heading can stick while its own options scroll past.
            withScrollArea={false}
            classNames={{
              input: timeInputClass,
              options: 'max-h-[240px] overflow-y-auto',
              // `!sticky` because Mantine's own groupLabel rule sets
              // `position: relative` and its stylesheet is imported unlayered,
              // so it outranks Tailwind's layered utilities.
              groupLabel: '!sticky top-0 z-10 bg-white',
            }}
            rightSection={
              reasonId ? undefined : <IconChevronDown size={14} className="opacity-60" />
            }
            placeholder="Select reason…"
            nothingFoundMessage="No reasons found"
            comboboxProps={{ withinPortal: true }}
          />
        </Box>
      )}

      {/* Date + start/end time */}
      <Box className="flex items-end gap-3.5">
        <Box className="flex h-[42px] shrink-0 items-center gap-[7px] text-[13.5px] font-bold whitespace-nowrap text-[#4A5160]">
          <IconCalendar size={16} stroke={1.9} className="text-[#8A929C]" />
          {formattedSlotDateStr}
        </Box>
        <Box className="flex min-w-0 flex-1 flex-col">
          <label className="mb-2 text-[13px] font-bold text-[#4A5160]">Start time</label>
          <Select
            name={`new-slot-start`}
            data={timeOptions.slice(0, -1)}
            value={slotStart}
            rightSection={<IconChevronDown size={14} className="opacity-60" />}
            classNames={{ input: timeInputClass }}
            placeholder="Select start time"
            onChange={(value) => handleSlotStartTimeUpdate(value)}
            comboboxProps={{ withinPortal: true }}
            allowDeselect={false}
          />
        </Box>
        <Box className="flex min-w-0 flex-1 flex-col">
          <label className="mb-2 text-[13px] font-bold text-[#4A5160]">End time</label>
          <Select
            name={`new-slot-end`}
            value={slotEnd}
            data={timeOptions.map((opt) => ({
              ...opt,
              disabled: slotStart ? opt.value <= slotStart : false,
            }))}
            classNames={{ input: timeInputClass }}
            rightSection={<IconChevronDown size={14} className="opacity-60" />}
            placeholder="Select end time"
            comboboxProps={{ withinPortal: true }}
            allowDeselect={false}
            onChange={handleSlotEndTimeUpdate}
          />
        </Box>
      </Box>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-end gap-[9px] border-t border-[#EEF0F3] pt-[18px]">
        <button
          type="button"
          onClick={handleCloseModal}
          className="schedule-v2-mulish rounded-md border border-[#D8DBE0] bg-white px-4 py-[5px] !text-[12px] !font-bold text-[#33383F] hover:bg-[#F4F6F8]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleTimeSlotAllocation}
          disabled={saveDisabled}
          className={`schedule-v2-mulish rounded-md px-4 py-[5px] !text-[12px] !font-bold ${
            saveDisabled
              ? 'cursor-not-allowed bg-[#F1F3F6] text-[#B0B6BE]'
              : 'bg-[#E5533D] text-white shadow-[0_1px_2px_rgba(203,71,31,0.3)] hover:bg-[#D4472F]'
          }`}
        >
          Save
        </button>
      </div>

      <AddNewPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={closeANPModal}
        successCallback={createNewPatientCallBack}
      />
    </Modal>
  )
}
